import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ServiceWorkerProvider } from './context/ServiceWorkerContext';
import { UpdateBanner } from './components/layout/UpdateBanner';
import { AppLayout } from './components/layout/AppLayout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { HeaderWithPlantCount } from './components/layout/HeaderWithPlantCount';
import { BottomNav } from './components/layout/BottomNav';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ScrollToTop } from './components/common/ScrollToTop';
import { AnimatedPage } from './components/common/AnimatedPage';
import { DiagnosticsPanel } from './components/common/DiagnosticsPanel';
import Login from './pages/Login';
import Home from './pages/Home';
import AddPlant from './pages/AddPlant';
import EditPlant from './pages/EditPlant';
import PlantDetail from './pages/PlantDetail';
import DiagnosePlant from './pages/DiagnosePlant';
import DiagnosisDetail from './pages/DiagnosisDetail';
import ShoppingList from './pages/ShoppingList';
import Settings from './pages/Settings';
import Activity from './pages/Activity';

// Show diagnostics panel in development or when URL has ?debug
const SHOW_DIAGNOSTICS = import.meta.env.DEV || new URLSearchParams(window.location.search).has('debug');

function ProtectedLayout() {
  return (
    <>
      <HeaderWithPlantCount />
      <div className="pb-20">
        <ProtectedRoute />
      </div>
      <BottomNav />
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/login" element={<AnimatedPage><Login /></AnimatedPage>} />

        {/* Protected routes with header + bottom nav */}
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<AnimatedPage><Home /></AnimatedPage>} />
          <Route path="/add" element={<AnimatedPage><AddPlant /></AnimatedPage>} />
          <Route path="/plant/:id" element={<AnimatedPage><PlantDetail /></AnimatedPage>} />
          <Route path="/plant/:id/edit" element={<AnimatedPage><EditPlant /></AnimatedPage>} />
          <Route path="/plant/:id/diagnose" element={<AnimatedPage><DiagnosePlant /></AnimatedPage>} />
          <Route path="/plant/:plantId/diagnosis/:diagnosisId" element={<AnimatedPage><DiagnosisDetail /></AnimatedPage>} />
          <Route path="/shopping" element={<AnimatedPage><ShoppingList /></AnimatedPage>} />
          <Route path="/settings" element={<AnimatedPage><Settings /></AnimatedPage>} />
          <Route path="/activity" element={<AnimatedPage><Activity /></AnimatedPage>} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ToastProvider>
            <ServiceWorkerProvider>
              <AppLayout>
                <AnimatedRoutes />
              </AppLayout>
              <UpdateBanner />
              {SHOW_DIAGNOSTICS && <DiagnosticsPanel />}
            </ServiceWorkerProvider>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
